import { create } from "zustand";

/** Drop-target discriminant (use enum members instead of raw strings). */
export enum ClinicArrivalOverTargetType {
    Task = "task",
    Unassigned = "unassigned",
}

export type ClinicArrivalTaskOverData = {
    type: ClinicArrivalOverTargetType.Task;
    taskId: string;
    zone: "outer" | "inner";
};

export type ClinicArrivalUnassignedOverData = {
    type: ClinicArrivalOverTargetType.Unassigned;
};

export type ClinicArrivalOverTarget = ClinicArrivalTaskOverData | ClinicArrivalUnassignedOverData;

/** Droppable id for the unassigned list / `movePassenger` target key. */
export enum ClinicArrivalDnDZoneId {
    Unassigned = "unassigned",
}

/** Prefix for task row / panel droppable ids (`task:{id}`, `task:{id}:inner`). */
export const CLINIC_ARRIVAL_TASK_DROP_PREFIX = "task:";

/** Passenger currently being dragged (overlay, placeholders). */
export type ClinicArrivalDragBasePayload = {
    id: string;
    name: string;
    uniqueIdentifier: string;
};

export enum ClinicArrivalDragPayloadTypes {
    FROM_UNASSIGNED = "fromUnassigned",
    FROM_TASK = "fromTask",
}
export type ClinicArrivalDragPayload = ClinicArrivalDragFromUnassignedPayload | ClinicArrivalDragFromTaskPayload;

export type ClinicArrivalDragFromTaskPayload = ClinicArrivalDragBasePayload & {
    type: ClinicArrivalDragPayloadTypes.FROM_TASK;
    taskId: string;
    /** Resolved passenger `listItemId` (PATCH `/hotel-clinic/tasks/:id/unassign` body). */
    listItemId: string;
};
export type ClinicArrivalDragFromUnassignedPayload = ClinicArrivalDragBasePayload & {
    type: ClinicArrivalDragPayloadTypes.FROM_UNASSIGNED;
};

/** Single DnD UI state: what is dragged + which drop target is highlighted (if any). */
export type ClinicArrivalDndSession = {
    drag: ClinicArrivalDragPayload;
    target: ClinicArrivalOverTarget | null;
};

type ClinicArrivalDndState = {
    overData: ClinicArrivalDndSession | null;
};

const initialState: ClinicArrivalDndState = {
    overData: null,
};

type ClinicArrivalDndActions = {
    actions: {
        beginDrag: (drag: ClinicArrivalDragPayload) => void;
        setTarget: (target: ClinicArrivalOverTarget | null) => void;
        resetSession: () => void;
    };
};

export const useClinicArrivalDndStore = create<ClinicArrivalDndState & ClinicArrivalDndActions>()((set) => ({
    ...initialState,
    actions: {
        beginDrag: (drag) => set({ overData: { drag, target: null } }),
        setTarget: (target) =>
            set((s) => {
                if (!s.overData) return s;
                return { overData: { ...s.overData, target } };
            }),
        resetSession: () => set({ overData: null }),
    },
}));

/** Full session (`null` when not dragging). */
export const useClinicArrivalDndOverData = () => useClinicArrivalDndStore((s) => s.overData);

export const useClinicArrivalDndDrag = () => useClinicArrivalDndStore((s) => s.overData?.drag ?? null);

export const useClinicArrivalIsOverType = (type: ClinicArrivalOverTargetType) =>
    useClinicArrivalDndStore((s) => s.overData?.target?.type && s.overData?.target?.type === type);

export const useClinicArrivalDndOverTaskId = () =>
    useClinicArrivalDndStore((s) =>
        s.overData?.target?.type === ClinicArrivalOverTargetType.Task ? s.overData.target.taskId : null
    );

export const useClinicArrivalDndOverUnassigned = () =>
    useClinicArrivalDndStore((s) => s.overData?.target?.type === ClinicArrivalOverTargetType.Unassigned);

export const useClinicArrivalDndActions = () => useClinicArrivalDndStore((s) => s.actions);
